import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useMatch } from 'react-router-dom';
import axios from '@raas/shared/src/utils/axios';
import { IDocumentElementDetails } from '@raas/shared/src/types/document-element';
import regulationsService from '@raas/shared/src/service/api/RegulationsService';
import {
  ILegalProvisionTranslation,
  IReadLegalProvision,
} from '@raas/shared/src/types/legal-provision';
import LegalProvisionContext from '@raas/shared/src/contexts/LegalProvisionContext';
import { LEGAL_PROVISION_STATE } from '@raas/shared/src/contexts/utils';
import { TTaskStatus, TTaskStatuses } from '@raas/shared/src/types/scheduled-task';

const key = 'legalProvisionContext';
const EntityIdHeader = 'Entity-Id';
const VersionIdHeader = 'Version-Id';
const VersionTimestampHeader = 'Version-Timestamp';
const ReleaseTimestampHeader = 'Release-Timestamp';

const areSameLegalProvisionContext = (
  previousContext: IReadLegalProvision | null,
  nextContext: IReadLegalProvision | null
) => {
  if (!previousContext || !nextContext) {
    return false;
  }

  return (
    previousContext.id === nextContext.id &&
    previousContext.entityId === nextContext.entityId &&
    previousContext.versionId === nextContext.versionId &&
    previousContext.versionTimestamp?.toISOString() ===
      nextContext.versionTimestamp?.toISOString() &&
    previousContext.releaseTimestamp?.toISOString() === nextContext.releaseTimestamp?.toISOString()
  );
};

type VersionContextProviderProps = {
  children: ReactNode;
  requiredPermission: 'INTERNAL_ACCESS' | 'EXTERNAL_ACCESS';
};

export function LegalProvisionContextProvider({
  children,
  requiredPermission,
}: VersionContextProviderProps) {
  const location = useLocation();
  const legalProvisionIdMatch = useMatch('/dashboard/legal-provision/:id/*');
  const regulationTreeMatch = useMatch('/dashboard/regulation/:id/tree');

  const isRouteScopedLegalProvision = Boolean(legalProvisionIdMatch || regulationTreeMatch);

  const [state, setState] = useState<IReadLegalProvision>(null);
  const [filteredRoot, setFilteredRoot] = useState<IDocumentElementDetails | null>(null);
  const [activeSearchMode, setActiveSearchMode] = useState(false);

  // legal provision translations
  const [currentTranslationId, setCurrentTranslationId] = useState<string>(null);

  const [originalLanguageId, setOriginalLanguageId] = useState<string>(null);
  const [selectedLanguageId, setSelectedLanguageId] = useState<string>(null);

  const [currentTranslation, setCurrentTranslation] = useState<ILegalProvisionTranslation | null>(
    null
  );
  const [translationStatuses, setTranslationStatuses] = useState<
    Record<string, { status: TTaskStatus; tasks: TTaskStatuses[] }>
  >({});

  const legalProvisionTranslationView = useMemo(
    () => ({
      originalLanguageId,
      setOriginalLanguageId,
      selectedLanguageId,
      setSelectedLanguageId,
    }),
    [originalLanguageId, selectedLanguageId]
  );

  const setLegalProvisionContext = useCallback((legalProvision: IReadLegalProvision) => {
    if (!legalProvision) {
      return;
    }

    localStorage.setItem(key, JSON.stringify(legalProvision));
    axios.defaults.headers.common[EntityIdHeader] = legalProvision.entityId;
    axios.defaults.headers.common[VersionIdHeader] = legalProvision.versionId;
    axios.defaults.headers.common[VersionTimestampHeader] =
      legalProvision.versionTimestamp?.toISOString();
    axios.defaults.headers.common[ReleaseTimestampHeader] =
      legalProvision.releaseTimestamp?.toISOString();

    setState((previousContext) => {
      if (areSameLegalProvisionContext(previousContext, legalProvision)) {
        return previousContext;
      }

      return legalProvision;
    });
  }, []);

  const clearLegalProvisionContext = useCallback(() => {
    localStorage.removeItem(key);

    delete axios.defaults.headers.common[EntityIdHeader];
    delete axios.defaults.headers.common[VersionIdHeader];
    delete axios.defaults.headers.common[VersionTimestampHeader];
    delete axios.defaults.headers.common[ReleaseTimestampHeader];

    setState(null);
  }, []);

  const entityIdHeaderValue = String(axios.defaults.headers.common[EntityIdHeader]);

  // legal state

  const [legalProvisionState, setLegalProvisionState] = useState({
    type: LEGAL_PROVISION_STATE.DEFAULT,
    elementId: '',
  });

  const value = useMemo(
    () => ({
      legalProvisionContext: state,
      setLegalProvisionContext,
      clearLegalProvisionContext,
      entityIdHeaderValue,
      requiredPermission,
      filteredRoot,
      setFilteredRoot,
      activeSearchMode,
      setActiveSearchMode,
      legalProvisionState,
      setLegalProvisionState,
      currentTranslationId,
      setCurrentTranslationId,
      legalProvisionTranslationView,
      translationStatuses,
      setTranslationStatuses,
      currentTranslation,
      setCurrentTranslation,
    }),
    [
      state,
      setLegalProvisionContext,
      clearLegalProvisionContext,
      entityIdHeaderValue,
      requiredPermission,
      filteredRoot,
      activeSearchMode,
      legalProvisionState,
      currentTranslationId,
      legalProvisionTranslationView,
      translationStatuses,
      currentTranslation,
    ]
  );

  useEffect(() => {
    const dateFormat = /(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/;
    const reviver = (_: string, valueToParse: any) =>
      typeof valueToParse === 'string' && dateFormat.test(valueToParse)
        ? new Date(valueToParse)
        : valueToParse;

    const abortController = new AbortController();

    const setContextFromLocalStorage = () => {
      const storedValue = localStorage.getItem(key);
      if (storedValue) {
        const storedState: IReadLegalProvision = JSON.parse(storedValue, reviver);
        if (storedState.versionId) {
          setLegalProvisionContext(storedState);
        }
      }
    };

    const setContextFromURL = async (
      queryParams: URLSearchParams,
      entityId: string,
      versionId: string
    ) => {
      try {
        const versionTimestamp = queryParams.get(VersionTimestampHeader);
        const releaseTimestamp = queryParams.get(ReleaseTimestampHeader);

        axios.defaults.headers.common[EntityIdHeader] = entityId;
        axios.defaults.headers.common[VersionIdHeader] = versionId;
        axios.defaults.headers.common[VersionTimestampHeader] = versionTimestamp;
        if (releaseTimestamp) {
          axios.defaults.headers.common[ReleaseTimestampHeader] = releaseTimestamp;
        }

        const legalProvisionsFromUrl =
          requiredPermission === 'EXTERNAL_ACCESS'
            ? await regulationsService.getAllAssigned(abortController.signal)
            : await regulationsService.getAll(abortController.signal);

        if (abortController.signal.aborted) {
          return;
        }

        let found = false;
        legalProvisionsFromUrl.forEach((legalProvision) => {
          if (
            legalProvision.entityId === entityId &&
            legalProvision.versionId === versionId &&
            ((versionTimestamp &&
              legalProvision.versionTimestamp?.toISOString() === versionTimestamp) ||
              !versionTimestamp) &&
            ((releaseTimestamp &&
              legalProvision.releaseTimestamp?.toISOString() === releaseTimestamp) ||
              !releaseTimestamp)
          ) {
            found = true;
            setLegalProvisionContext(legalProvision);
          }
        });
        if (!found) {
          clearLegalProvisionContext();
        }
      } catch (error) {
        // Silently handle abort errors
        if (error?.name !== 'AbortError') {
          console.error('Error loading legal provisions from URL:', error);
        }
      }
    };

    const setContext = () => {
      // On routes that already include a legal provision id, the page-level data loader
      // is the source of truth and should not be overridden by URL/localStorage hydration.
      if (isRouteScopedLegalProvision) {
        return;
      }

      const queryParams = new URLSearchParams(window.location.search);
      const entityId = queryParams.get(EntityIdHeader);
      const versionId = queryParams.get(VersionIdHeader);

      const urlDiffersFromState =
        entityId && versionId && (state?.entityId !== entityId || state?.versionId !== versionId);

      if (urlDiffersFromState) {
        setContextFromURL(queryParams, entityId, versionId);
      } else if (!entityId || !versionId) {
        setContextFromLocalStorage();
      }
    };

    setContext();

    return () => {
      abortController.abort();
    };
  }, [
    setLegalProvisionContext,
    clearLegalProvisionContext,
    location.search,
    isRouteScopedLegalProvision,
    state,
    requiredPermission,
  ]);

  return <LegalProvisionContext.Provider value={value}>{children}</LegalProvisionContext.Provider>;
}
